import React from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  Hash,
  MapPin,
  FileText,
  Calendar,
  DollarSign,
  Briefcase,
  Activity,
} from 'lucide-react'
import { useSearchTokens } from '../../SearchScreen/searchDesignTokens'

const CompanyHeader = ({ company }) => {
  const { t, i18n } = useTranslation()
  const dk = useSearchTokens()

  if (!company) return null

  const lang = i18n.language?.split('-')[0]
  const getLocalizedText = (textObj) => {
    if (!textObj) return null
    if (typeof textObj === 'string') return textObj
    return textObj[lang] || textObj.en || textObj.fr || textObj.ar || null
  }

  const formatCapital = (capital, devise) => {
    if (!capital) return null
    return `${Number(capital).toLocaleString('fr-FR')} ${devise || 'MAD'}`
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return null
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const infoItems = [
    { icon: <Hash size={14} />, label: 'RC', value: company.registre_commerce },
    { icon: <FileText size={14} />, label: 'ICE', value: company.ice },
    { icon: <MapPin size={14} />, label: t('ville', 'Ville'), value: company.ville },
    { icon: <Calendar size={14} />, label: t('date_creation', 'Création'), value: formatDate(company.date_creation) },
    { icon: <DollarSign size={14} />, label: t('capital', 'Capital'), value: formatCapital(company.capital_social, company.devise) },
    { icon: <Activity size={14} />, label: t('statut', 'Statut'), value: company.statut_societe ? t(`statut_${company.statut_societe}`, company.statut_societe) : null },
  ].filter((item) => item.value)

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: '12px',
        border: `1px solid ${dk.border}`,
        backgroundColor: dk.surface,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${dk.gold}, transparent)`,
        },
      }}
    >
      {/* Company name + badges */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '10px',
            backgroundColor: dk.goldSub,
            border: `1px solid ${dk.goldBdr}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Building2 size={22} color={dk.gold} />
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: dk.text1,
              fontSize: { xs: '1.15rem', md: '1.35rem' },
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            {company.denomination || '-'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {company.forme_juridique && (
              <Chip
                label={t(`forme_${company.forme_juridique}`, company.forme_juridique)}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: dk.gold,
                  backgroundColor: dk.goldSub,
                  border: `1px solid ${dk.goldBdr}`,
                }}
              />
            )}
            {company.secteur_principal && (
              <Chip
                label={t(`secteur_${company.secteur_principal}`, company.secteur_principal)}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  color: dk.text2,
                  backgroundColor: dk.chipBg,
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Info grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 1.5,
          pt: 2,
          borderTop: `1px solid ${dk.border}`,
        }}
      >
        {infoItems.map(({ icon, label, value }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: dk.text3, display: 'flex' }}>{icon}</Box>
            <Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                {value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Objet social */}
      {getLocalizedText(company.objet_social) && (
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${dk.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
            <Briefcase size={13} color={dk.text3} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t('objet_social', 'Objet social')}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '0.82rem',
              color: dk.text2,
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {getLocalizedText(company.objet_social)}
          </Typography>
        </Box>
      )}

      {/* Siege social */}
      {getLocalizedText(company.siege_social) && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
            <MapPin size={13} color={dk.text3} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t('siege_social', 'Siège social')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.82rem', color: dk.text2, lineHeight: 1.5 }}>
            {getLocalizedText(company.siege_social)}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default CompanyHeader
